import React, { memo, forwardRef } from 'react';
import ReactEChartsCore from '../EChart';
import * as echarts from 'echarts/core';
import { BarChart } from 'echarts/charts';
import {
  TooltipComponent,
  GridComponent,
  LegendComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { Card } from 'react-bootstrap';
import { useAppContext } from '../../../providers/AppProvider';

echarts.use([
  BarChart,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  CanvasRenderer
]);

const toNum = v => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const getOptions = (
  theme,
  getThemeColor,
  rows,
  unit,
  {
    labelKey = 'label',
    valueKey = 'value',
    labelFormatter = v => String(v ?? '—'),
    valueFormatter = v => toNum(v),
    barWidth = 16, // ✅ bare mai mici
    rotateLabels = 30
  } = {}
) => {
  const categories = (rows || []).map(r => labelFormatter(r?.[labelKey], r));
  const values = (rows || []).map(r => valueFormatter(r?.[valueKey], r));

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: params => {
        const it = params?.[0];
        const val = toNum(it?.value);
        return `<strong>${it?.name}</strong><br/>${val.toFixed(2)} ${unit}`;
      }
    },
    xAxis: {
      type: 'category',
      data: categories,
      axisLabel: {
        rotate: rotateLabels,
        fontSize: 11,
        color: getThemeColor('gray-800')
      },
      axisLine: { lineStyle: { color: getThemeColor('gray-300') } }
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        fontSize: 12,
        formatter: `{value} ${unit}`,
        color: getThemeColor('gray-800')
      },
      splitLine: { lineStyle: { color: getThemeColor('gray-300') } }
    },
    series: [
      {
        type: 'bar',
        data: values,
        barWidth, // ✅ aici controlezi “grosimea” barelor
        itemStyle: { color: getThemeColor('primary') },
        label: {
          show: true,
          position: 'top',
          fontSize: 10,
          color: getThemeColor('gray-800'),
          formatter: p => `${toNum(p.value).toFixed(1)} ${unit}`
        }
      }
    ],
    grid: {
      left: '5%',
      right: '5%',
      bottom: '20%',
      top: '10%',
      containLabel: true
    }
  };
};

const MaterialTotalsArrayChart = forwardRef(
  (
    {
      data = [],
      title,
      unit = 'kg',
      labelKey = 'label',
      valueKey = 'value',
      labelFormatter,
      valueFormatter,
      barWidth = 16,
      rotateLabels = 30
    },
    ref
  ) => {
    const {
      config: { theme },
      getThemeColor
    } = useAppContext();

    return (
      <Card className="h-100">
        <Card.Body>
          <div style={styles.headerContainer}>
            <div style={styles.title}>{title}</div>
          </div>

          <ReactEChartsCore
            ref={ref}
            echarts={echarts}
            option={getOptions(theme, getThemeColor, data, unit, {
              labelKey,
              valueKey,
              labelFormatter,
              valueFormatter,
              barWidth,
              rotateLabels
            })}
            style={{ height: '400px', width: '100%' }}
          />
        </Card.Body>
      </Card>
    );
  }
);

const styles = {
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px'
  },
  title: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#333'
  }
};

export default memo(MaterialTotalsArrayChart);
